<script setup lang="ts">
  import { useMediaControls } from '@vueuse/core'
  import type { VideoBannerStoryblok } from '@/storyblok/component-types-sb'
  import { transformSBUrl } from '@/utils/sbDataParsers'

  const props = defineProps<{
    blok: VideoBannerStoryblok
  }>()

  const dayjs = useDayjs()

  const { external, slug, video, thumbnail, title, publishDate } = toRefs(props.blok)

  const url = external?.value ? transformSBUrl(slug?.value?.url) : transformSBUrl(video?.value?.filename)

  const posterUrl = computed(() => transformSBUrl(thumbnail?.value?.filename))

  const videoElem = ref()
  const { playing, currentTime } = useMediaControls(videoElem, {
    src: url
  })

  const isPlayed = computed(() => !!currentTime.value)

  const handlePlay = () => {
    playing.value = true
  }

  const jsonLD = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: title.value,
    uploadDate: dayjs(publishDate.value).toISOString(),
    thumbnailUrl: [posterUrl.value],
    contentUrl: url
  }))

  useHead({
    script: [
      {
        id: `video-banner-json-ld-${props.blok._uid}`,
        type: 'application/ld+json',
        innerHTML: JSON.stringify(jsonLD.value)
      }
    ]
  })
</script>

<template>
  <div
    v-if="url"
    v-editable="blok"
    class="relative aspect-video w-full overflow-hidden rounded-md"
  >
    <template v-if="external">
      <iframe
        :src="url"
        title="YouTube video player"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerpolicy="strict-origin-when-cross-origin"
        allowfullscreen
        class="aspect-video w-full"
      />
    </template>
    <template v-else>
      <video
        ref="videoElem"
        :controls="isPlayed"
        class="size-full object-cover"
        :poster="posterUrl"
        playsinline
        controlslist="nodownload"
      />
      <button
        v-if="!isPlayed"
        class="absolute left-0 top-0 flex size-full cursor-pointer items-center justify-center"
        @click="handlePlay"
      >
        <Icon
          name="common/play"
          class="md-32 rounded-full bg-bt-green p-2"
        />
      </button>
    </template>
  </div>
</template>
